import { useQuery } from "@tanstack/react-query";
import { natsContract } from "@common/contracts/nats.contract";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const natsContractClient = getBackendAPIClient(natsContract);

const natsQueryKeys = {
    all: () => ["nats"],
    jwt: (organizationId?: string | null, workspaceId?: string | null) => [
        ...natsQueryKeys.all(),
        "jwt",
        organizationId,
        workspaceId,
    ],
} as const;

export const fetchWorkspaceNatsJwt = async (workspaceId: string) => {
    const result = await natsContractClient.getNatsJwt({
        params: { workspaceId },
    });

    return result.body;
};

export function useNatsJwt() {
    const { organizationId, workspaceId } = useScope();

    return useQuery({
        queryKey: natsQueryKeys.jwt(organizationId, workspaceId),
        queryFn: async () => {
            if (organizationId) {
                const query = workspaceId ? { workspaceId } : {};
                const result = await natsContractClient.getOrganizationNatsJwt({
                    params: { organizationId },
                    query,
                });

                return result.body;
            }

            if (!workspaceId) {
                throw new Error("Organization ID or workspace ID is required");
            }

            return await fetchWorkspaceNatsJwt(workspaceId);
        },
        enabled: !!organizationId || !!workspaceId,
        staleTime: 30 * 60 * 1000, // Consider data stale after 30 minutes
        refetchInterval: 45 * 60 * 1000, // Refetch every 45 minutes to get fresh token
    });
}
